import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PreloadFill from '@/components/LoadingSkeleton/PreloadFill';
import { useEffect, useState, useRef } from 'react';
import { processHero } from './processHero';
import { processCTAGeneric } from './processCTAGeneric';
import { processWorkWithUs } from './processWorkWithUs';
import { processTestimonials } from './processTestimonials';
import Testimonials from '../../cta/CTATestimonials';
import CTAWorkWithUs from '../../cta/CTAWorkWithUs';
import CTAGeneric from '../../cta/CTAGeneric';
import showConsole from '@/utils/showConsole';



interface PageContentProps {
  id: string;
  className?: string;
  containerClass?: string;
}

interface IPageContent {
  status: string;
  statuscode: number;
  result: {
    id: string;
    name: string;
    content: string;
  };
}


const GetPageContent = async (id: string): Promise<IPageContent> => {
  try {
    const res = await axios.get<IPageContent>(
      `https://www.harveynormancommercial.nz/api/v1/page/?id=${id}`,
      {
        timeout: 10000, // 10 second timeout
        headers: {
          'Accept': 'application/json',
        }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error fetching page content:', error);
    throw error;
  }
};

function htmlDecode(input: string): string {
  if (!input || typeof input !== 'string') {
    console.warn('Invalid input provided to htmlDecode');
    return '';
  }

  try {
    // Simple decode - just handle URL encoding and basic HTML entities
    let doc = decodeURIComponent(input);

    // Basic HTML entity decoding only
    doc = doc.replace(/&lt;/g, "<");
    doc = doc.replace(/&gt;/g, ">");
    doc = doc.replace(/&quot;/g, '"');
    doc = doc.replace(/&#39;/g, "'");
    doc = doc.replace(/&amp;/g, "&"); // Handle & last to avoid double decoding

    return doc;
  } catch (error) {
    console.error('Error decoding HTML:', error);
    return input; // Return original input if decoding fails
  }
}



function convertTailwindArbitraryToInline(html: string): string {
  try {
    // Parse HTML to work with DOM elements
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find all elements with class attributes
    const elementsWithClasses = doc.querySelectorAll('[class]');

    elementsWithClasses.forEach(element => {
      // Check if className is a string before splitting
      const className = element.className;
      if (typeof className !== 'string') {
        return; // Skip this element if className is not a string
      }

      const classList = className.split(' ');
      let inlineStyles: string[] = [];
      let remainingClasses: string[] = [];

      classList.forEach(className => {
        // Convert bg-[url(path)] background images
        const bgUrlMatch = className.match(/^bg-\[url\(([^)]+)\)\]$/);
        if (bgUrlMatch) {
          inlineStyles.push(`background-image: url(${bgUrlMatch[1]})`);
          return;
        }

        // Convert other arbitrary value patterns
        const conversions = [
          { pattern: /^w-\[([^\]]+)\]$/, style: 'width' },
          { pattern: /^h-\[([^\]]+)\]$/, style: 'height' },
          { pattern: /^bg-\[#([a-fA-F0-9]{3,6})\]$/, style: 'background-color', prefix: '#' },
          { pattern: /^text-\[#([a-fA-F0-9]{3,6})\]$/, style: 'color', prefix: '#' },
          { pattern: /^top-\[([^\]]+)\]$/, style: 'top' },
          { pattern: /^left-\[([^\]]+)\]$/, style: 'left' },
          { pattern: /^right-\[([^\]]+)\]$/, style: 'right' },
          { pattern: /^bottom-\[([^\]]+)\]$/, style: 'bottom' },
          { pattern: /^z-\[([^\]]+)\]$/, style: 'z-index' },
        ];

        let converted = false;
        conversions.forEach(({ pattern, style, prefix = '' }) => {
          const match = className.match(pattern);
          if (match && !converted) {
            inlineStyles.push(`${style}: ${prefix}${match[1]}`);
            converted = true;
          }
        });

        // If not converted, keep the original class
        if (!converted) {
          remainingClasses.push(className);
        }
      });

      // Apply inline styles
      if (inlineStyles.length > 0) {
        const existingStyle = element.getAttribute('style') || '';
        const newStyle = existingStyle + (existingStyle ? '; ' : '') + inlineStyles.join('; ');
        element.setAttribute('style', newStyle);
      }

      // Update class attribute with remaining classes
      if (remainingClasses.length > 0) {
        element.setAttribute('class', remainingClasses.join(' '));
      } else {
        element.removeAttribute('class');
      }
    });

    // After all Tailwind conversions, process hero overlay/blur
    return processHero(doc.body.innerHTML);
  } catch (error) {
    console.error('Error converting Tailwind arbitrary values:', error);
    return html; // Return original if conversion fails
  }
}

export default function RenderContentPage({
  id,
  className = '',
  containerClass = ''
}: PageContentProps) {
  // Unified state for processed HTML and all extracted dynamic data
  const [processed, setProcessed] = useState<{
    html: string | null;
    dynamic: Record<string, any>;
  }>({ html: null, dynamic: {} });
  const contentRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['PageContent', id],
    queryFn: () => GetPageContent(id),
    enabled: !!id && id.trim() !== '', // Only fetch if id is valid
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Garbage collect after 10 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 404s
      if (failureCount < 2) {
        const status = (error as any)?.response?.status;
        return status !== 404 && status !== 403;
      }
      return false;
    }
  });


  // Process content and testimonials when data changes
  // Utility to detect section IDs in raw HTML using DOMParser
  // Utility to extract and log section HTML by id
  function extractAndLogSections(rawHtml: string) {
    const result: Record<string, string | null> = {
      testimonials: null,
      workwithus: null,
      question: null,
    };
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawHtml, 'text/html');
      ['testimonials', 'workwithus', 'question'].forEach((id) => {
        const el = doc.getElementById(id);
        if (el) {
          result[id] = el.outerHTML;
        }
      });
    } catch (e) {
      // if (process.env.NODE_ENV === 'development') {
      if (showConsole) {
        // eslint-disable-next-line no-console
        console.warn('[SectionExtraction] DOMParser failed:', e);
      }
    }
    // if (process.env.NODE_ENV === 'development') {
    if (showConsole) {
      // eslint-disable-next-line no-console
      Object.entries(result).forEach(([id, html]) => {
        if (html) {
          console.log(`[SectionExtraction] Found <section id="${id}">:`, html.slice(0, 200) + (html.length > 200 ? '...' : ''));
        } else {
          console.log(`[SectionExtraction] No <section id="${id}"> found.`);
        }
      });
    }
    return result;
  }

  useEffect(() => {
    if (data?.result?.content) {
      // if (process.env.NODE_ENV === 'development') {
      if (showConsole) {
        console.log('Processing page content for ID:', id);
      }
      let decoded = htmlDecode(data.result.content);
      // Extract and log section HTML before any processing
      const extractedSections = extractAndLogSections(decoded);

      // Process testimonials section and inject rendered HTML into the section
      if (extractedSections.testimonials) {
        const { html: testimonialsHtml, testimonialsSlides } = processTestimonials(extractedSections.testimonials);
        // Replace the contents of the original section with the rendered HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(decoded, 'text/html');
        const section = doc.getElementById('testimonials');
        if (section) {
          section.innerHTML = testimonialsHtml;
          // if (process.env.NODE_ENV === 'development') {
          if (showConsole) {
            // eslint-disable-next-line no-console
            console.log('[SectionProcess] Injected testimonials HTML:', testimonialsHtml.slice(0, 200) + (testimonialsHtml.length > 200 ? '...' : ''));
          }
          // Update decoded to use this new HTML for further processing if needed
          decoded = doc.body.innerHTML;
        }
      }
      decoded = convertTailwindArbitraryToInline(decoded);

      // Compose processors, accumulating html and dynamic data
      let html = decoded;
      const dynamic: Record<string, any> = {};

      // Testimonials
      const testimonialsResult = processTestimonials(html);
      html = testimonialsResult.html;
      dynamic['TESTIMONIALS_PLACEHOLDER'] = { slides: testimonialsResult.testimonialsSlides };


      // WorkWithUs
      const workWithUsResult = processWorkWithUs(html);
      html = workWithUsResult.html;
      dynamic['WORKWITHUS_PLACEHOLDER'] = workWithUsResult.workWithUsData;

      // Generic CTA (question)
      const ctaGenericResult = processCTAGeneric(html);
      html = ctaGenericResult.html;
      dynamic['CTAGENERIC_PLACEHOLDER'] = ctaGenericResult.ctaGenericData;

      setProcessed({ html, dynamic });
    } else {
      setProcessed({ html: null, dynamic: {} });
    }
  }, [data, id]);

  // Update browser tab title from CMS page name
  useEffect(() => {
    if (data?.result?.name) {
      document.title = `${data.result.name} | Harvey Norman Commercial NZ`;
    }
  }, [data?.result?.name]);

  // Removed useEffect that directly manipulates container.innerHTML to allow React components to render properly

  // Handle error states
  const renderError = () => {
    const errorStatus = (error as any)?.response?.status;
    const errorMessage = errorStatus === 404
      ? 'Content not found'
      : 'Failed to load content';

    return (
      <div className={`flex items-center justify-center p-4 ${containerClass}`}>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md text-center">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline ml-1">{errorMessage}</span>
        </div>
      </div>
    );
  };

  // Handle loading state
  if (isLoading) {
    // Use PreloadFill to fill the space between header and footer while loading
    return <PreloadFill className={containerClass} showLoader={true} loaderMessage='' />
  }

  // Handle error state
  if (isError) {
    return renderError();
  }

  // Handle empty content
  if (!processed.html) {
    return (
      <div className={`flex items-center justify-center p-4 ${containerClass}`}>
        <div className="text-gray-500 text-center">
          <p>No content available</p>
        </div>
      </div>
    );
  }

  // Render processed HTML, injecting React components at placeholders
  if (processed.html) {
    // Split HTML at placeholders for dynamic React components
    const html = processed.html;
    const testimonialsKey = '<!--TESTIMONIALS_PLACEHOLDER-->';
    const workWithUsKey = '<!--WORKWITHUS_PLACEHOLDER-->';
    const ctaGenericKey = '<!--CTAGENERIC_PLACEHOLDER-->';

    // Split for testimonials
    const [beforeTestimonials, afterTestimonials] = html.split(testimonialsKey);
    // Split for workwithus (after testimonials)
    const [beforeWorkWithUs, afterWorkWithUs] = (afterTestimonials || '').split(workWithUsKey);
    // Split for cta generic (after workwithus)
    const [beforeCTAGeneric, afterCTAGeneric] = (afterWorkWithUs || '').split(ctaGenericKey);

    // Extract dynamic data for each section
    const testimonialsData = processed.dynamic['TESTIMONIALS_PLACEHOLDER'];
    const workWithUsData = processed.dynamic['WORKWITHUS_PLACEHOLDER'];
    const ctaGenericData = processed.dynamic['CTAGENERIC_PLACEHOLDER'];

    return (
      <div className={containerClass} ref={contentRef}>
        {/* Before testimonials section */}
        {beforeTestimonials && <div dangerouslySetInnerHTML={{ __html: beforeTestimonials }} />}

        {/* Testimonials React component */}
        {testimonialsData?.slides && testimonialsData.slides.length > 0 && (
          <Testimonials testimonials={testimonialsData.slides} />
        )}

        {/* Between testimonials and workwithus */}
        {beforeWorkWithUs && <div dangerouslySetInnerHTML={{ __html: beforeWorkWithUs }} />}


        {/* WorkWithUs React component */}
        {workWithUsData && (
          <CTAWorkWithUs {...workWithUsData} />
        )}

        {/* Between workwithus and cta generic */}
        {beforeCTAGeneric && <div dangerouslySetInnerHTML={{ __html: beforeCTAGeneric }} />}


        {/* CTAGeneric React component */}
        {ctaGenericData && (
          <CTAGeneric {...ctaGenericData} />
        )}

        {/* After all dynamic sections */}
        {afterCTAGeneric && <div dangerouslySetInnerHTML={{ __html: afterCTAGeneric }} />}
      </div>
    );
  }
  // If no processedContent, show nothing (should not happen due to earlier checks)
  return null;
}